/**
 * Component for booking a tee time
 */
import React, { useState } from 'react';
import { useTeeTime } from '../hooks/useTeeTime';
import { Player } from '../types';
import { formatDateLong } from '../utils/dateUtils';
import '../styles/BookingForm.css';

/**
 * Default form state for a player
 */
const defaultPlayerState: Omit<Player, 'id'> = {
  name: '',
  email: '',
  phone: '',
};

/**
 * BookingForm component for reserving tee times
 * @returns React component
 */
const BookingForm: React.FC = () => {
  const { selectedTeeTime, selectedDate, selectedCourse, createBooking } = useTeeTime();
  
  const [playerCount, setPlayerCount] = useState<number>(1);
  const [players, setPlayers] = useState<Omit<Player, 'id'>[]>([{ ...defaultPlayerState }]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [bookingConfirmation, setBookingConfirmation] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  
  /**
   * Updates a player's information
   * @param index Index of the player to update
   * @param field Field to update
   * @param value New value
   */
  const handlePlayerChange = (
    index: number, 
    field: keyof Omit<Player, 'id'>, 
    value: string
  ) => {
    const updatedPlayers = [...players];
    updatedPlayers[index] = {
      ...updatedPlayers[index],
      [field]: value,
    };
    setPlayers(updatedPlayers);
  };
  
  /**
   * Updates the number of players and adjusts the player array
   * @param count New player count
   */
  const handlePlayerCountChange = (count: number) => {
    setPlayerCount(count);
    
    // Adjust the players array based on the new count
    if (count > players.length) {
      // Add more player forms
      const additionalPlayers = Array(count - players.length)
        .fill(null)
        .map(() => ({ ...defaultPlayerState }));
      
      setPlayers([...players, ...additionalPlayers]);
    } else if (count < players.length) {
      // Remove excess player forms
      setPlayers(players.slice(0, count));
    }
  };
  
  /**
   * Validates the booking form
   * @returns True if form is valid, false otherwise
   */
  const validateForm = (): boolean => {
    // Reset error
    setBookingError(null);
    
    // Check if a tee time is selected
    if (!selectedTeeTime) {
      setBookingError('Please select a tee time first');
      return false;
    }
    
    // Check if player count is valid
    if (playerCount < 1 || playerCount > 4) {
      setBookingError('Player count must be between 1 and 4');
      return false;
    }
    
    // Check if player count exceeds available spots
    if (playerCount > selectedTeeTime.availableSpots) {
      setBookingError(`Only ${selectedTeeTime.availableSpots} spots available for this tee time`);
      return false;
    }
    
    // Validate each player's information
    for (let i = 0; i < playerCount; i++) {
      const player = players[i];
      
      if (!player.name.trim()) {
        setBookingError(`Player ${i + 1}: Name is required`);
        return false;
      }
      
      if (!player.email.trim()) {
        setBookingError(`Player ${i + 1}: Email is required`);
        return false;
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(player.email)) {
        setBookingError(`Player ${i + 1}: Invalid email format`);
        return false;
      }
      
      // Phone validation is optional
      if (player.phone && !/^\d{10}$/.test(player.phone.replace(/\D/g, ''))) {
        setBookingError(`Player ${i + 1}: Invalid phone number format`);
        return false;
      }
    }
    
    return true;
  };
  
  /**
   * Handles form submission to book a tee time
   * @param e Form submit event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate the form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setBookingError(null);
    setBookingConfirmation(null);
    
    try {
      if (!selectedTeeTime || !selectedCourse) {
        throw new Error('No tee time or course selected');
      }
      
      // Create the booking
      const booking = await createBooking(
        selectedCourse.id,
        selectedTeeTime.id,
        players.slice(0, playerCount)
      );
      
      if (booking) {
        // Show confirmation
        setBookingConfirmation(
          `Success! Your tee time has been booked. Confirmation code: ${booking.confirmationCode}`
        );
        
        // Reset the form
        setPlayerCount(1);
        setPlayers([{ ...defaultPlayerState }]);
      } else {
        setBookingError('Failed to book tee time. Please try again.');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setBookingError('An error occurred while booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!selectedTeeTime) {
    return (
      <div className="booking-form-empty">
        <h2>Book a Tee Time</h2>
        <div className="booking-steps">
          <div className="booking-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Select a Date</h3>
              <p>Choose your preferred date from the calendar</p>
            </div>
          </div>
          <div className="booking-step active">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Select a Tee Time</h3>
              <p>Click on a tee time from the list on the left</p>
            </div>
          </div>
          <div className="booking-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Complete Booking</h3>
              <p>Fill out your information and confirm</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="booking-form">
      <h2>Book a Tee Time</h2>
      
      {bookingConfirmation ? (
        <div className="booking-confirmation">
          <div className="booking-confirmation-message">{bookingConfirmation}</div>
          <button
            className="booking-new-button"
            onClick={() => setBookingConfirmation(null)}
          >
            Book Another Tee Time
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="booking-details">
            <div className="booking-detail-item">
              <span className="booking-detail-label">Date:</span>
              <span className="booking-detail-value">{formatDateLong(selectedDate)}</span>
            </div>
            <div className="booking-detail-item">
              <span className="booking-detail-label">Time:</span>
              <span className="booking-detail-value">
                {selectedTeeTime.startTime} - {selectedTeeTime.endTime}
              </span>
            </div>
            <div className="booking-detail-item">
              <span className="booking-detail-label">Available Spots:</span>
              <span className="booking-detail-value">{selectedTeeTime.availableSpots} of 4</span>
            </div>
          </div>
          
          <div className="booking-form-group">
            <label htmlFor="playerCount">Number of Players:</label>
            <select
              id="playerCount"
              value={playerCount}
              onChange={(e) => handlePlayerCountChange(Number(e.target.value))}
              disabled={isSubmitting}
            >
              {[...Array(Math.min(4, selectedTeeTime.availableSpots))].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
          
          {players.slice(0, playerCount).map((player, index) => (
            <div key={index} className="player-form">
              <h3>Player {index + 1}</h3>
              
              <div className="booking-form-group">
                <label htmlFor={`name-${index}`}>Name:</label>
                <input
                  id={`name-${index}`}
                  type="text"
                  value={player.name}
                  onChange={(e) => handlePlayerChange(index, 'name', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="booking-form-group">
                <label htmlFor={`email-${index}`}>Email:</label>
                <input
                  id={`email-${index}`}
                  type="email"
                  value={player.email}
                  onChange={(e) => handlePlayerChange(index, 'email', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="booking-form-group">
                <label htmlFor={`phone-${index}`}>Phone (optional):</label>
                <input
                  id={`phone-${index}`}
                  type="tel"
                  value={player.phone}
                  onChange={(e) => handlePlayerChange(index, 'phone', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          ))}
          
          {bookingError && <div className="booking-error">{bookingError}</div>}
          
          <button 
            type="submit" 
            className="booking-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Booking...' : 'Book Tee Time'}
          </button>
        </form>
      )}
    </div>
  );
};

export default BookingForm;
